import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { useApiCaller } from '../utils/ApiUtilities';
import { useUser } from '../contexts/UserContext';
import {
    getPlansUrl,
    getPurchaseQuoteUrl,
    getPurchaseUrl,
    getSavedCardsUrl,
} from '../utils/UrlUtilities';
import { constructPurchasePayload } from '../utils/PayloadUtils';
import { PLAN_LABELS } from '../utils/Constants';
import { formatBdPst } from '../utils/DateUtilities';
import { isEmpty, formatUsdBdt } from '../utils/BasicUtilities';

const PREMIUM_TIER_GRADIENT = 'linear-gradient(145deg,#7c3aed,#5b21b6)';

// Payment methods shown on the buy page. Card (Stripe) and PayPal are live; bKash is a
// placeholder (coming soon).
const PAY_METHODS = [
    { id: 'stripe', label: 'International Card', hint: 'Visa · Mastercard · Amex — charged in USD', icon: 'bi-credit-card-2-front', enabled: true },
    { id: 'paypal', label: 'PayPal', hint: 'Pay with your PayPal balance or card — charged in USD', icon: 'bi-paypal', enabled: true },
    { id: 'bkash', label: 'bKash', hint: 'Coming soon — for Bangladesh (৳)', icon: 'bi-phone', enabled: false },
];

export default function SubscriptionDetail() {
    const { planCode } = useParams();
    const [params] = useSearchParams();
    const CallApi = useApiCaller();
    const { userState } = useUser();

    const [plan, setPlan] = useState(null);
    const [quote, setQuote] = useState(null);
    const [promoInput, setPromoInput] = useState((params.get('promo') || '').toUpperCase());
    const [appliedPromo, setAppliedPromo] = useState((params.get('promo') || '').toUpperCase());
    const [agreeRefund, setAgreeRefund] = useState(false);
    const [method, setMethod] = useState('stripe');
    const [upgradeTarget, setUpgradeTarget] = useState(null); // 'current' | 'scheduled'
    const [cards, setCards] = useState([]);
    const [selectedCard, setSelectedCard] = useState('new'); // 'new' or a saved card id
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Load quote for the current applied promo + upgrade target (server-authoritative pricing).
    const loadQuote = useCallback(async (promo, target) => {
        const p = { plan_code: planCode };
        if (!isEmpty(promo)) p.promo_code = promo;
        if (target) p.upgrade_target = target;
        const res = await CallApi({ url: getPurchaseQuoteUrl(p), method: 'GET', token: true });
        if (res.success) setQuote(res.data);
        return res;
    }, [CallApi, planCode]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const [plansRes] = await Promise.all([
                CallApi({ url: getPlansUrl(), method: 'GET', token: false }),
                loadQuote(appliedPromo, null),
            ]);
            if (plansRes.success) {
                setPlan((plansRes.data?.plans || []).find(p => p.code === planCode) || null);
            }
            const cardsRes = await CallApi({ url: getSavedCardsUrl(), method: 'GET', token: true });
            if (cardsRes.success) {
                const list = cardsRes.data || [];
                setCards(list);
                const def = list.find(c => c.is_default) || list[0];
                if (def) setSelectedCard(String(def.card_id));
            }
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planCode]);

    const handleApplyPromo = async () => {
        const code = promoInput.trim().toUpperCase();
        setAppliedPromo(code);
        await loadQuote(code, upgradeTarget);
    };

    const handleSelectTarget = async (t) => {
        setUpgradeTarget(t);
        await loadQuote(appliedPromo, t);
    };

    const handlePurchase = async () => {
        setSubmitting(true);
        const usingSavedCard = method === 'stripe' && selectedCard && selectedCard !== 'new';
        const res = await CallApi({
            url: getPurchaseUrl(),
            method: 'POST',
            payload: constructPurchasePayload({
                planCode,
                promoCode: quote?.promo_valid ? appliedPromo : null,
                provider: method,
                savedCardId: usingSavedCard ? selectedCard : null,
                upgradeTarget: quote?.mode === 'UPGRADE' ? (upgradeTarget || quote?.target) : null,
            }),
            token: true,
        });
        if (!res.success) {
            setSubmitting(false); // error already surfaced via ErrorContext
            return;
        }
        const data = res.data || {};
        // Saved card needing 3DS: authenticate with Stripe.js, then land on the result page
        // (which polls purchase-status and self-heals the grant).
        if (data.requires_action && data.client_secret && data.publishable_key) {
            try {
                const stripe = await loadStripe(data.publishable_key);
                await stripe.confirmCardPayment(data.client_secret);
            } catch (e) {
                // Ignore — the result page shows the authoritative final status.
            }
            window.location = `/subscription/result?status=success&tran_id=${encodeURIComponent(data.tran_id)}`;
            return;
        }
        if (data.redirect_url) {
            window.location = data.redirect_url; // Stripe hosted page, or the result page for a saved card
            return;
        }
        setSubmitting(false);
    };

    if (!userState?.isLoggedIn) {
        return <div className="container py-5 text-center text-muted">Please log in to purchase a plan.</div>;
    }
    if (loading) {
        return <div className="container py-5 text-center"><div className="spinner-border text-primary" role="status" /></div>;
    }
    if (!plan) {
        return <div className="container py-5 text-center text-muted">Plan not found. <Link to="/subscription">Back to plans</Link></div>;
    }

    const blocked = quote && quote.allowed === false;
    const hasDiscount = quote && quote.promo_valid && quote.discount_bdt > 0;
    const referralDiscount = (quote && quote.referral_discount_bdt) || 0;
    const anyDiscount = hasDiscount || referralDiscount > 0;

    return (
        <div className="container py-4" style={{ maxWidth: 720 }}>
            <Link to="/subscription" className="small text-muted">← Back to plans</Link>
            <div className="card mt-2" style={{ border: '1px solid #ede9fe', borderRadius: '0.75rem', overflow: 'hidden' }}>
                <div className="text-center" style={{ background: PREMIUM_TIER_GRADIENT, color: '#fff', padding: '1rem' }}>
                    <h3 className="mb-0">{PLAN_LABELS[plan.code] || plan.name}</h3>
                    <div style={{ opacity: 0.9, fontSize: '0.9rem' }}>{plan.duration_days} days</div>
                </div>
                <div className="card-body">
                    <ul className="list-unstyled" style={{ fontSize: '0.9rem' }}>
                        {(plan.features || []).map((b, i) => (
                            <li key={i} className="mb-2"><i className="bi bi-check-circle-fill text-success me-2" />{b}</li>
                        ))}
                    </ul>

                    {blocked ? (
                        <div className="alert alert-warning">{quote.reason}</div>
                    ) : (
                        <>
                            {/* Price summary */}
                            <div className="border rounded p-3 mb-3 bg-light">
                                {quote?.mode === 'UPGRADE' && (
                                    <div className="small text-muted mb-1">
                                        Upgrade from {PLAN_LABELS[quote.current_plan_code] || quote.current_plan_code} — you only pay the difference.
                                    </div>
                                )}
                                <div className="d-flex justify-content-between">
                                    <span>{quote?.mode === 'UPGRADE' ? 'Upgrade price' : 'Price'}</span>
                                    <span>
                                        {anyDiscount && (
                                            <span className="text-muted text-decoration-line-through me-2">{formatUsdBdt(quote?.base_price_usd, quote.base_price_bdt)}</span>
                                        )}
                                        <strong style={{ color: '#4c1d95' }}>
                                            {formatUsdBdt(
                                                quote ? quote.final_price_usd : plan.price_usd,
                                                quote ? quote.final_price_bdt : plan.price_bdt,
                                            )}
                                        </strong>
                                    </span>
                                </div>
                                {hasDiscount && (
                                    <div className="text-success small mt-1">
                                        Price discounted with promo code <strong>{appliedPromo}</strong> (−৳{quote.discount_bdt}).
                                    </div>
                                )}
                                {referralDiscount > 0 && (
                                    <div className="text-success small mt-1">
                                        🎁 Referral discount applied (−৳{referralDiscount}) — thanks for joining through a friend!
                                    </div>
                                )}
                            </div>

                            {/* Upgrade target — only when both a current and an upcoming plan exist */}
                            {quote?.available_targets && quote.available_targets.length > 1 && (
                                <div className="mb-3">
                                    <label className="form-label small fw-semibold">Which plan do you want to upgrade?</label>
                                    <div className="d-flex flex-column gap-2">
                                        {quote.available_targets.map(t => {
                                            const sel = (upgradeTarget || quote.target) === t.target;
                                            return (
                                                <button
                                                    key={t.target}
                                                    type="button"
                                                    onClick={() => handleSelectTarget(t.target)}
                                                    className="text-start p-2 border rounded"
                                                    style={{
                                                        background: sel ? '#5b21b6' : '#fff',
                                                        color: sel ? '#fff' : 'inherit',
                                                        borderColor: sel ? '#5b21b6' : '#e5e7eb',
                                                        borderWidth: sel ? 2 : 1,
                                                    }}
                                                >
                                                    <span className="fw-semibold d-block">
                                                        {t.target === 'current' ? 'Upgrade current plan (activates now)' : 'Upgrade upcoming plan'}
                                                    </span>
                                                    <span style={{ fontSize: '0.8rem', color: sel ? 'rgba(255,255,255,0.85)' : '#6c757d' }}>
                                                        From {PLAN_LABELS[t.plan_code] || t.plan_code} · pay ৳{t.price_bdt} · {formatBdPst(t.starts_at, { withTime: false })} → {formatBdPst(t.expires_at, { withTime: false })}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Coverage window — clearly shown in green, message depends on mode */}
                            {quote?.starts_at && quote?.expires_at && (
                                <div className="border rounded p-3 mb-3" style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                                    {quote.mode !== 'UPGRADE' && (
                                        <div className="small fw-semibold" style={{ color: '#15803d' }}>
                                            {quote.mode === 'NEW'
                                                ? '✅ Starts today'
                                                : `🗓️ Starts after your current plan ends (${PLAN_LABELS[quote.current_plan_code] || quote.current_plan_code})`}
                                        </div>
                                    )}
                                    <div className="mt-1" style={{ color: '#166534', fontWeight: 600 }}>
                                        {formatBdPst(quote.starts_at, { withTime: false })}
                                        <span className="mx-2">→</span>
                                        {formatBdPst(quote.expires_at, { withTime: false })}
                                    </div>
                                    {(quote.mode === 'RENEW' || quote.mode === 'DOWNGRADE') && (
                                        <div className="small text-muted mt-1">
                                            You keep your current plan until then — this one is queued to follow it.
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Promo */}
                            <label className="form-label small fw-semibold">Have a promo code?</label>
                            <div className="input-group mb-1">
                                <input
                                    className="form-control"
                                    style={{ textTransform: 'uppercase' }}
                                    placeholder="Enter code"
                                    value={promoInput}
                                    onChange={(e) => setPromoInput(e.target.value)}
                                />
                                <button className="btn btn-outline-primary" type="button" onClick={handleApplyPromo} disabled={isEmpty(promoInput)}>
                                    Apply
                                </button>
                            </div>
                            {quote && !quote.promo_valid && quote.promo_note && (
                                <div className="text-danger small mb-2">{quote.promo_note}</div>
                            )}

                            {/* Payment method */}
                            <label className="form-label small fw-semibold mt-2">Payment method</label>
                            <div className="d-flex flex-column gap-2 mb-3">
                                {PAY_METHODS.map(m => {
                                    const selected = method === m.id;
                                    return (
                                        <button
                                            key={m.id}
                                            type="button"
                                            onClick={() => m.enabled && setMethod(m.id)}
                                            disabled={!m.enabled}
                                            className="text-start d-flex align-items-center gap-3 p-2 border rounded"
                                            style={{
                                                background: selected ? '#5b21b6' : '#fff',
                                                color: selected ? '#fff' : 'inherit',
                                                borderColor: selected ? '#5b21b6' : '#e5e7eb',
                                                borderWidth: selected ? 2 : 1,
                                                cursor: m.enabled ? 'pointer' : 'not-allowed',
                                                opacity: m.enabled ? 1 : 0.6,
                                            }}
                                        >
                                            <i className={`bi ${m.icon}`} style={{ fontSize: '1.3rem', color: selected ? '#fff' : '#5b21b6' }} />
                                            <span className="flex-grow-1">
                                                <span className="fw-semibold d-block">{m.label}</span>
                                                <span style={{ fontSize: '0.78rem', color: selected ? 'rgba(255,255,255,0.85)' : '#6c757d' }}>{m.hint}</span>
                                            </span>
                                            {!m.enabled && <span className="badge bg-secondary">Soon</span>}
                                            {selected && <i className="bi bi-check-circle-fill" style={{ color: '#fff' }} />}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Saved cards (Stripe) — pick one or use a new card */}
                            {method === 'stripe' && cards.length > 0 && (
                                <>
                                    <label className="form-label small fw-semibold">Card</label>
                                    <select className="form-select mb-3" value={selectedCard} onChange={(e) => setSelectedCard(e.target.value)}>
                                        {cards.map(c => (
                                            <option key={c.card_id} value={c.card_id}>
                                                {c.brand ? `${c.brand} ` : ''}•••• {c.last4}{c.is_default ? ' — default' : ''}
                                            </option>
                                        ))}
                                        <option value="new">Use a new card…</option>
                                    </select>
                                </>
                            )}

                            {/* Non-refundable acknowledgement */}
                            <div className="form-check my-3">
                                <input className="form-check-input" type="checkbox" id="refundAck" checked={agreeRefund} onChange={(e) => setAgreeRefund(e.target.checked)} />
                                <label className="form-check-label" htmlFor="refundAck">
                                    I understand this purchase is <strong>non-refundable</strong>.
                                </label>
                            </div>

                            <button
                                className="btn w-100"
                                style={{ background: PREMIUM_TIER_GRADIENT, color: '#fff', fontWeight: 600 }}
                                onClick={handlePurchase}
                                disabled={submitting || !agreeRefund}
                            >
                                {submitting ? 'Processing…' : `Pay ${formatUsdBdt(quote ? quote.final_price_usd : plan.price_usd, quote ? quote.final_price_bdt : plan.price_bdt)}`}
                            </button>
                            <div className="text-center text-muted mt-2" style={{ fontSize: '0.72rem' }}>
                                {method === 'paypal'
                                    ? "You'll be redirected to PayPal to approve the payment. Charged in USD."
                                    : method === 'stripe' && selectedCard && selectedCard !== 'new'
                                        ? 'Charged to your saved card via Stripe (USD).'
                                        : "You'll be redirected to Stripe's secure page to enter your card details. Charged in USD."}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
